import { useState } from 'react';
import { AlertTriangle, CheckCircle, Smartphone, Mail } from 'lucide-react';

export default function DeleteAccountPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="mx-auto max-w-2xl px-6 py-12">
        <div className="space-y-6">
          <Intro />
          <InAppMethod />
          <EmailMethod />
          <DataNote />
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <div className="bg-indigo-600 py-12 px-6">
      <div className="mx-auto max-w-2xl">
        <a href="/" className="mb-4 inline-block text-sm text-indigo-200 hover:text-white transition-colors">
          &larr; Back to home
        </a>
        <h1 className="text-3xl font-bold text-white">Delete Account</h1>
        <p className="mt-2 text-indigo-200">You can delete your Lambada account at any time.</p>
      </div>
    </div>
  );
}

function Intro() {
  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm text-gray-600 leading-relaxed">
      <div className="flex gap-3 items-start">
        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-gray-800 mb-1">Before you delete</p>
          <p className="text-sm">
            Deleting your account permanently removes your profile, messages, photos, and all
            associated data. <strong className="text-gray-800">This action cannot be undone.</strong>
          </p>
          <p className="text-sm mt-2">
            If you just need a break, you can instead set your availability to{' '}
            <span className="font-medium text-gray-800">Unavailable</span> in your profile — your
            account will auto-delete after 180 days of inactivity anyway.
          </p>
        </div>
      </div>
    </div>
  );
}

function InAppMethod() {
  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50">
          <Smartphone className="h-5 w-5 text-indigo-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Delete via the app (fastest)</h2>
      </div>
      <ol className="space-y-3 text-sm text-gray-600">
        <Step n={1} text='Open the Lambada app and go to your Profile tab.' />
        <Step n={2} text='Tap the settings icon in the top-right corner.' />
        <Step n={3} text='Scroll down and tap "Delete Account".' />
        <Step n={4} text='Confirm the deletion. Your account and all data are removed immediately.' />
      </ol>
    </div>
  );
}

function Step({ n, text }: { n: number; text: string }) {
  return (
    <li className="flex gap-3">
      <span className="flex-shrink-0 mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-600">
        {n}
      </span>
      <span>{text}</span>
    </li>
  );
}

function EmailMethod() {
  const [submitted, setSubmitted] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!identifier.trim()) {
      setError('Please enter your phone number or email address.');
      return;
    }
    setError('');

    const subject = encodeURIComponent('Account Deletion Request');
    const body = encodeURIComponent(
      `I would like to delete my Lambada account.\n\nPhone / Email: ${identifier.trim()}\nReason: ${reason.trim() || 'Not specified'}`
    );
    window.location.href = `mailto:support@fibosolutions.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
  }

  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50">
          <Mail className="h-5 w-5 text-indigo-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Request deletion by email</h2>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        If you no longer have access to the app, fill in the form below. We will process your
        request within <strong className="text-gray-700">7 business days</strong>.
      </p>

      {submitted ? (
        <div className="flex items-center gap-3 rounded-xl bg-green-50 px-4 py-3 text-sm text-green-800">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
          Your email client should have opened. If not, email us directly at{' '}
          <a href="mailto:support@fibosolutions.com" className="font-medium underline">
            support@fibosolutions.com
          </a>.
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone number or email address <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="+84 901 234 567 or you@example.com"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
            {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Let us know why you're leaving (optional)"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none"
            />
          </div>
          <button
            type="submit"
            className="w-full rounded-xl bg-indigo-600 py-3 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
          >
            Send deletion request
          </button>
        </form>
      )}
    </div>
  );
}

function DataNote() {
  return (
    <div className="rounded-2xl bg-gray-100 px-5 py-4 text-sm text-gray-500 leading-relaxed">
      <strong className="text-gray-700">What gets deleted:</strong> your profile, display name,
      phone/email, profile photo, portfolio images, messages, and location data. Abuse reports you
      have filed are retained for up to 1 year for safety purposes, per our{' '}
      <a href="/privacy" className="text-indigo-600 hover:underline">
        Privacy Policy
      </a>.
    </div>
  );
}

function Footer() {
  return (
    <div className="border-t border-gray-200 py-6 px-6">
      <p className="text-center text-sm text-gray-400">
        &copy; {new Date().getFullYear()} Lambada &mdash;{' '}
        <a href="/privacy" className="hover:text-indigo-600 transition-colors">Privacy Policy</a>
      </p>
    </div>
  );
}
